import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

/* Signature capture — HTML canvas the user can sign with a finger
 * (mobile) or mouse (desktop). Falls back to a typed-name input so
 * users without a touch device or signature pad can still complete the
 * acknowledgement; both forms are equally valid for audit. */
function SignaturePad({ value, onChange }) {
  const canvasRef = useRef(null);
  const drawing   = useRef(false);
  const lastPt    = useRef(null);
  const [isEmpty, setIsEmpty] = useState(true);

  // Match the canvas backing-store resolution to its CSS size × devicePixelRatio
  // so strokes stay crisp on retina / high-DPI screens.
  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const dpr = window.devicePixelRatio || 1;
    const rect = c.getBoundingClientRect();
    c.width  = rect.width  * dpr;
    c.height = rect.height * dpr;
    const ctx = c.getContext("2d");
    ctx.scale(dpr, dpr);
    ctx.lineWidth   = 2;
    ctx.lineCap     = "round";
    ctx.lineJoin    = "round";
    ctx.strokeStyle = "#0f172a";
  }, []);

  const pointFromEvent = (e) => {
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    const t = e.touches?.[0] || e.changedTouches?.[0];
    const clientX = t ? t.clientX : e.clientX;
    const clientY = t ? t.clientY : e.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const start = (e) => {
    e.preventDefault();
    drawing.current = true;
    lastPt.current  = pointFromEvent(e);
  };

  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const p   = pointFromEvent(e);
    ctx.beginPath();
    ctx.moveTo(lastPt.current.x, lastPt.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastPt.current = p;
    if (isEmpty) setIsEmpty(false);
  };

  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    lastPt.current  = null;
    // Hand the data URL up so the parent can submit it. PNG keeps the
    // signature visible against any background and stays under a few
    // KB for typical strokes.
    onChange(canvasRef.current.toDataURL("image/png"));
  };

  const clear = () => {
    const c   = canvasRef.current;
    const ctx = c.getContext("2d");
    ctx.clearRect(0, 0, c.width, c.height);
    setIsEmpty(true);
    onChange("");
  };

  return (
    <div className="su-signature-wrap">
      <canvas
        ref={canvasRef}
        className="su-signature-canvas"
        onMouseDown={start}
        onMouseMove={move}
        onMouseUp={end}
        onMouseLeave={end}
        onTouchStart={start}
        onTouchMove={move}
        onTouchEnd={end}
      />
      <div className="su-signature-footer">
        <span className="su-signature-hint">
          {isEmpty ? "Sign above using your finger or mouse" : "Signature captured"}
        </span>
        <button type="button" className="su-link-btn" onClick={clear} disabled={isEmpty}>
          Clear
        </button>
      </div>
    </div>
  );
}

/* In-page modal that loads the venue's T&Cs URL inside an iframe.
 * Lives at <body> via a portal so the signup card's overflow rules
 * can't clip it. ESC and clicking the backdrop both close. */
function TermsModal({ url, title, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    // Lock body scroll while open so the iframe scroll captures input.
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  return createPortal(
    <div className="su-modal-backdrop" onMouseDown={onClose}>
      <div className="su-modal" onMouseDown={(e) => e.stopPropagation()}>
        <div className="su-modal-header">
          <h3 className="su-modal-title">{title}</h3>
          <button type="button" className="su-modal-close" onClick={onClose} aria-label="Close">×</button>
        </div>
        <iframe
          src={url}
          title={title}
          className="su-modal-iframe"
          sandbox="allow-same-origin allow-scripts allow-forms allow-popups"
        />
        <div className="su-modal-footer">
          <a href={url} target="_blank" rel="noopener noreferrer" className="su-link-btn">
            Trouble viewing? Open in a new tab ↗
          </a>
          <button type="button" className="su-btn su-btn-primary" onClick={onClose}>Done</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default function StepTerms({ venue, onAccept, onBack, initialAcceptance }) {
  const [accepted, setAccepted]     = useState(!!initialAcceptance?.accepted);
  const [signature, setSignature]   = useState(initialAcceptance?.signature || "");
  const [error, setError]           = useState("");
  const [modalOpen, setModalOpen]   = useState(false);

  // A drawn signature on the canvas is required to proceed. The
  // typed-name fallback that lived here was removed — members on a
  // mobile device always have touch input, and the optional print-name
  // capture confused users into thinking they had a way around the
  // signature box.
  const hasSignature = !!signature;
  // Signature is required by default; a venue can turn it off so the Terms
  // step is a plain acknowledgement tick with no signature pad.
  const requireSignature = venue?.terms_require_signature !== false;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!accepted) {
      setError("Please tick the box to acknowledge the Terms & Conditions.");
      return;
    }
    if (requireSignature && !hasSignature) {
      setError("Please sign in the box to continue.");
      return;
    }
    onAccept({
      accepted:   true,
      version:    venue?.terms_version || null,
      signature:  signature || null,
      signedName: null,
    });
  };

  const title = venue?.terms_title || "Terms & Conditions";
  const body  = (venue?.terms_body || "").trim();
  const url   = (venue?.terms_url  || "").trim();
  // Auto-detect display mode from which field the venue admin filled.
  // No body → URL is the canonical document; show an "open in new tab"
  // button instead of an empty scrollable box. When both are present we
  // prefer the inline body and keep the URL as a supplementary link.
  const useUrl  = !body && !!url;
  // Modal vs new-tab is a per-venue preference. PDFs almost never embed
  // in iframes — defensive check here mirrors the admin-side lock so a
  // stale `modal` setting on a new PDF URL still falls back to new tab.
  const isPdf      = /\.pdf(\?|#|$)/i.test(url);
  const useModal   = useUrl && venue?.terms_display_mode === "modal" && !isPdf;

  return (
    <>
      <h2 className="step-title">{title}</h2>
      <p className="step-sub">
        {useUrl
          ? "Please open the membership Terms & Conditions, then acknowledge below."
          : "Please review and accept the membership Terms & Conditions before continuing."}
      </p>

      {error && <div className="su-alert error">⚠ {error}</div>}

      <form onSubmit={handleSubmit}>
        {/* URL mode — host the document on the venue's own website.
            Opens either inside an in-page modal or in a fresh browser
            tab depending on the venue's display preference. */}
        {useUrl && (
          <div className="su-terms-url-card">
            <p style={{ margin: "0 0 0.85rem 0", fontSize: "0.92rem", color: "var(--text-secondary, #475569)" }}>
              {useModal
                ? `Please read the full ${title} document below before acknowledging and signing.`
                : `The full ${title} document is available on the venue's website. Click below to read it in a new tab, then return here to acknowledge and sign.`}
            </p>
            {useModal ? (
              <button
                type="button"
                onClick={() => setModalOpen(true)}
                className="su-btn su-btn-outline"
                style={{ display: "inline-flex", alignItems: "center", gap: "0.5rem" }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="18" height="18" rx="2" />
                  <line x1="3" y1="9" x2="21" y2="9" />
                </svg>
                Open Terms &amp; Conditions
              </button>
            ) : (
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="su-btn su-btn-outline"
                style={{ display: "inline-flex", alignItems: "center", gap: "0.5rem" }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                  <polyline points="15 3 21 3 21 9" />
                  <line x1="10" y1="14" x2="21" y2="3" />
                </svg>
                Open Terms &amp; Conditions
              </a>
            )}
          </div>
        )}

        {modalOpen && useModal && (
          <TermsModal url={url} title={title} onClose={() => setModalOpen(false)} />
        )}

        {/* Inline mode — admin pasted the full text into the venue. */}
        {!useUrl && body && (
          <div className="su-terms-body" tabIndex={0}>
            {body.split(/\n{2,}/).map((para, i) => (
              <p key={i}>{para}</p>
            ))}
          </div>
        )}

        {/* Optional supplementary link when inline body is the primary source. */}
        {!useUrl && url && (
          <p className="su-terms-link">
            A full copy is also available at{" "}
            <a href={url} target="_blank" rel="noopener noreferrer">{url}</a>.
          </p>
        )}

        <div className="su-field" style={{ marginTop: "1.25rem" }}>
          <label className="su-checkbox-row">
            <input
              type="checkbox"
              checked={accepted}
              onChange={(e) => { setAccepted(e.target.checked); if (e.target.checked) setError(""); }}
            />
            <span>
              I have read and agree to the {title}
              {venue?.terms_version ? ` (${venue.terms_version})` : ""}.
            </span>
          </label>
        </div>

        {requireSignature && (
          <>
            <div className="su-divider">Signature</div>
            <SignaturePad value={signature} onChange={(v) => { setSignature(v); if (v) setError(""); }} />
          </>
        )}

        <div className="su-btn-row">
          <button type="button" className="su-btn su-btn-outline" onClick={onBack}>Back</button>
          <button type="submit" className="su-btn su-btn-primary" disabled={!accepted || (requireSignature && !hasSignature)}>
            Accept &amp; Continue
          </button>
        </div>
      </form>
    </>
  );
}
